import type {
  Collaborator,
  ExcalElement,
  ExcalidrawApi,
} from "../scenes/excalidrawTypes";

// Live-collaboration client for an Excalidraw scene.  Connects to the API's
// WebSocket relay (api/src/ws/collab.ts), broadcasts local element deltas +
// pointer, applies remote deltas via Excalidraw's element-level reconciliation
// (version/versionNonce last-write-wins — no CRDT), and renders remote cursors
// through Excalidraw's collaborators map.  Durable persistence is Replicache's
// job (the editor autosaves the reconciled scene); this carries no durable
// state.

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15_000;
const ELEMENT_THROTTLE_MS = 50;
const POINTER_THROTTLE_MS = 50;
const WS_OPEN = 1;

type Outbound =
  | { t: "element-update"; elements: ExcalElement[] }
  | {
      t: "pointer-update";
      pointer: { x: number; y: number };
      button: "up" | "down";
      selectedIds?: Record<string, boolean>;
    }
  | { t: "request-init" }
  | { t: "scene-init"; elements: ExcalElement[]; to: string };

export type CollabCallbacks = {
  getApi: () => ExcalidrawApi | null;
  onCanEdit: (canEdit: boolean) => void;
  onCollaborators: (m: Map<string, Collaborator>) => void;
  /** Remote elements just merged into the scene.  The relay carries only the
   *  element (with its `fileId`), not the image bytes, so the host uses this to
   *  fetch any referenced files from MinIO and feed them to addFiles(). */
  onRemoteElements?: (elements: ExcalElement[]) => void;
};

export class SceneCollab {
  private ws: WebSocket | null = null;
  private closed = false;
  private reconnectAttempts = 0;
  private canEdit = false;
  private collaborators = new Map<string, Collaborator>();
  // id -> last broadcast versionNonce, to diff outbound changes
  private lastSent = new Map<string, number>();
  private elementTimer: number | null = null;
  private pendingElements: Map<string, ExcalElement> | null = null;
  private pointerTimer: number | null = null;
  private pendingPointer: Outbound | null = null;

  constructor(
    private sceneId: string,
    private getToken: () => string | null,
    private cb: CollabCallbacks,
  ) {}

  start(): void {
    this.connect();
  }

  destroy(): void {
    this.closed = true;
    if (this.elementTimer !== null) window.clearTimeout(this.elementTimer);
    if (this.pointerTimer !== null) window.clearTimeout(this.pointerTimer);
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  private wsUrl(): string | null {
    const token = this.getToken();
    if (!token) return null;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${location.host}/api/collab?scene=${encodeURIComponent(
      this.sceneId,
    )}&token=${encodeURIComponent(token)}`;
  }

  private connect(): void {
    if (this.closed) return;
    const url = this.wsUrl();
    if (!url) {
      // No token yet (still bootstrapping) — try again shortly.
      window.setTimeout(() => this.connect(), 1000);
      return;
    }
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.lastSent.clear();
      this.send({ t: "request-init" });
    };
    ws.onmessage = (ev) => this.onMessage(ev);
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      // Drop remote cursors on disconnect.
      this.collaborators.clear();
      this.cb.onCollaborators(new Map());
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** this.reconnectAttempts,
      RECONNECT_MAX_MS,
    );
    this.reconnectAttempts++;
    window.setTimeout(() => this.connect(), delay);
  }

  private send(obj: Outbound): void {
    const ws = this.ws;
    if (ws && ws.readyState === WS_OPEN) {
      try {
        ws.send(JSON.stringify(obj));
      } catch {
        /* dead socket; onclose will reconnect */
      }
    }
  }

  /** Broadcast local element changes (called from Excalidraw onChange).  Diffs
   *  by versionNonce so only changed elements go out; throttled. */
  broadcastElements(elements: readonly ExcalElement[]): void {
    if (!this.canEdit) return;
    const changed: ExcalElement[] = [];
    for (const el of elements) {
      const nonce = el.versionNonce ?? el.version ?? 0;
      if (this.lastSent.get(el.id) !== nonce) {
        this.lastSent.set(el.id, nonce);
        changed.push(el);
      }
    }
    if (changed.length === 0) return;
    if (!this.pendingElements) this.pendingElements = new Map();
    for (const el of changed) this.pendingElements.set(el.id, el);
    if (this.elementTimer === null) {
      this.elementTimer = window.setTimeout(() => {
        this.elementTimer = null;
        if (this.pendingElements) {
          this.send({
            t: "element-update",
            elements: [...this.pendingElements.values()],
          });
          this.pendingElements = null;
        }
      }, ELEMENT_THROTTLE_MS);
    }
  }

  /** Broadcast local pointer position (from Excalidraw onPointerUpdate). */
  broadcastPointer(
    pointer: { x: number; y: number },
    button: "up" | "down",
    selectedIds?: Record<string, boolean>,
  ): void {
    this.pendingPointer = { t: "pointer-update", pointer, button, selectedIds };
    if (this.pointerTimer === null) {
      this.pointerTimer = window.setTimeout(() => {
        this.pointerTimer = null;
        if (this.pendingPointer) {
          this.send(this.pendingPointer);
          this.pendingPointer = null;
        }
      }, POINTER_THROTTLE_MS);
    }
  }

  private localElements(api: ExcalidrawApi): readonly ExcalElement[] {
    return api.getSceneElementsIncludingDeleted
      ? api.getSceneElementsIncludingDeleted()
      : api.getSceneElements();
  }

  private applyRemote(remote: ExcalElement[]): void {
    const api = this.cb.getApi();
    if (!api) return;
    const merged = reconcile(this.localElements(api), remote);
    api.updateScene({ elements: merged });
    // Record incoming versions so we don't echo them back as "changed".
    for (const el of remote) {
      this.lastSent.set(el.id, el.versionNonce ?? el.version ?? 0);
    }
    // Image bytes aren't relayed (only the element + its fileId).  Hand the
    // remote elements to the host so it can load any missing files from MinIO.
    this.cb.onRemoteElements?.(remote);
  }

  private onMessage(ev: MessageEvent): void {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    if (!msg || typeof msg !== "object") return;

    switch (msg.t) {
      case "hello":
        this.canEdit = !!msg.canEdit;
        this.cb.onCanEdit(this.canEdit);
        break;

      case "presence": {
        const socketId = msg.socketId;
        if (typeof socketId !== "string") break;
        if (msg.event === "join") {
          const u = (msg.user ?? {}) as {
            name?: string;
            color?: { background: string; stroke: string };
          };
          this.collaborators.set(socketId, {
            username: u.name,
            color: u.color,
          });
        } else if (msg.event === "leave") {
          this.collaborators.delete(socketId);
        }
        this.cb.onCollaborators(new Map(this.collaborators));
        break;
      }

      case "pointer-update": {
        const socketId = msg.socketId;
        if (typeof socketId !== "string") break;
        const existing = this.collaborators.get(socketId) ?? {};
        this.collaborators.set(socketId, {
          ...existing,
          pointer: msg.pointer as { x: number; y: number } | undefined,
          button: msg.button as "up" | "down" | undefined,
          selectedElementIds: msg.selectedIds as
            | Record<string, boolean>
            | undefined,
        });
        this.cb.onCollaborators(new Map(this.collaborators));
        break;
      }

      case "element-update":
        if (Array.isArray(msg.elements)) {
          this.applyRemote(msg.elements as ExcalElement[]);
        }
        break;

      case "request-init": {
        // A peer wants our current snapshot.
        const api = this.cb.getApi();
        const from = msg.from;
        if (!api || typeof from !== "string") break;
        this.send({
          t: "scene-init",
          elements: [...this.localElements(api)],
          to: from,
        });
        break;
      }

      case "scene-init":
        if (Array.isArray(msg.elements)) {
          this.applyRemote(msg.elements as ExcalElement[]);
        }
        break;
    }
  }
}

/** Element-level last-write-wins merge: higher version wins; ties broken by the
 *  smaller versionNonce (Excalidraw's deterministic rule). */
function reconcile(
  local: readonly ExcalElement[],
  remote: ExcalElement[],
): ExcalElement[] {
  const byId = new Map<string, ExcalElement>();
  for (const el of local) byId.set(el.id, el);
  for (const el of remote) {
    const cur = byId.get(el.id);
    if (!cur || isRemoteNewer(el, cur)) byId.set(el.id, el);
  }
  return [...byId.values()];
}

function isRemoteNewer(remote: ExcalElement, local: ExcalElement): boolean {
  const rv = remote.version ?? 0;
  const lv = local.version ?? 0;
  if (rv !== lv) return rv > lv;
  return (remote.versionNonce ?? 0) < (local.versionNonce ?? 0);
}
